import { SlugAnalysis, TCardFacet, TFacet } from '@/types/collection'
import { logger } from '@/lib/logger'
import { FilterValues } from '../types/filter-store-v2.types'
import { findMatchingFacetValue } from './facet-matching'

/**
 * Utility functions for URL operations
 */

/**
 * Detect base path from current pathname
 */
export const detectBasePath = (pathname?: string): string => {
  if (typeof window !== 'undefined') {
    return window.location.pathname.includes('/collection/')
      ? '/collection'
      : '/collection'
  }
  return pathname?.includes('/collection/') ? '/collection' : '/collection'
}

/**
 * Create default SlugAnalysis
 */
export const createDefaultSlugAnalysis = (
  originalSlug: string
): SlugAnalysis => ({
  seo_alias: '',
  original_slug_from_client: originalSlug,
  attributes_count: 0,
  attributes: {},
  size: null,
  color: null,
  has_concat_value: false,
  concat: '',
})

/**
 * Build query parameters from filter values
 */
export const buildQueryParams = (
  filters: FilterValues
): Record<string, string | number> => {
  return Object.entries(filters).reduce(
    (acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          acc[key] = value.join(',')
        } else {
          acc[key] = value
        }
      }
      return acc
    },
    {} as Record<string, string | number>
  )
}

/**
 * Parse URL to extract filters
 */
export const parseUrlToFilters = (
  url: URL,
  originalSlug: string,
  facets?: TFacet[],
  cardFacets?: TCardFacet[]
): Record<string, string | string[]> => {
  const urlFilters: Record<string, string | string[]> = {}

  // Parse query parameters
  url.searchParams.forEach((value, key) => {
    if (key === 'limit') return

    // Handle multiple values (comma-separated)
    if (value.includes(',')) {
      urlFilters[key] = value.split(',')
    } else {
      urlFilters[key] = value
    }
  })

  // Parse slug-concatenated filters
  const pathSlug = url.pathname.replace(/^\/(?:[a-z]{2}\/)?collection\//, '')
  if (pathSlug && pathSlug !== originalSlug) {
    // Extract filter values from slug
    const slugParts = pathSlug.split('-')
    const originalParts = originalSlug?.split('-') || []

    // Find the difference (new filter values)
    const newParts = slugParts.slice(originalParts.length)
    if (newParts.length > 0) {
      const concatValue = newParts.join('-')

      // Map concat value to actual facet keys if facets data is available
      if (facets || cardFacets) {
        const allFacets = [...(facets || []), ...(cardFacets || [])]
        let mapped = false

        // Try to find exact matching facet value for the concat value

        for (const facet of allFacets) {
          if (!facet?.is_concat_slug) continue

          // Check if concat value exactly matches any facet value
          const matchingValue = facet.facet_values?.find(
            (value) => value.seo_alias === concatValue
          )

          if (matchingValue) {
            // Map to the actual facet key
            urlFilters[facet.seo_alias] = matchingValue.seo_alias
            logger.info('✅ [parseUrlToFilters] Exact match found:', {
              context: {
                facet: facet.seo_alias,
                value: matchingValue.seo_alias,
                title: matchingValue.title,
              },
            })
            mapped = true
            break
          }
        }

        // If no exact match found, try partial matching for complex concat values
        if (!mapped) {
          const match = findMatchingFacetValue(
            concatValue,
            facets || [],
            cardFacets || []
          )
          if (match) {
            urlFilters[match.facet.seo_alias] = match.value.seo_alias

            mapped = true
          }
        }

        // If no mapping found, keep as slug_concat for fallback
        if (!mapped) {
          urlFilters['slug_concat'] = concatValue
        }
      } else {
        // Fallback: keep as slug_concat if no facets data
        urlFilters['slug_concat'] = concatValue
      }
    }
  }

  return urlFilters
}

/**
 * Extract concat value from URL pathname
 */
export const extractConcatValueFromUrl = (
  pathname: string,
  originalSlug: string
): string | null => {
  const pathSlug = pathname.replace(/^\/(?:[a-z]{2}\/)?collection\//, '')
  if (!pathSlug || pathSlug === originalSlug) {
    return null
  }

  const slugParts = pathSlug.split('-')
  const originalParts = originalSlug.split('-')

  // Find the difference (new filter values)
  const newParts = slugParts.slice(originalParts.length)
  if (newParts.length > 0) {
    // Return full remaining parts joined with hyphen (supports values like "choi-the-thao")
    return newParts.join('-')
  }

  return null
}

/**
 * Clean URL by removing page=1 and empty params
 */
export const cleanUrl = (url: URL): string => {
  // Remove page=1
  if (url.searchParams.get('page') === '1') {
    url.searchParams.delete('page')
  }

  return url.pathname + (url.search ? url.search : '')
}

/**
 * Get current URL as URL object
 */
export const getCurrentUrl = (): URL => {
  return new URL(window.location.href)
}

/**
 * Update URL using history API
 * Uses replaceState to avoid creating new history entries for filter changes
 */
export const updateUrl = (newUrl: string): void => {
  window.history.replaceState(null, '', newUrl)
}

/**
 * Navigate to new URL (creates new history entry)
 * Use for actual navigation, not filter changes
 */
export const navigateToUrl = (newUrl: string): void => {
  window.history.pushState(null, '', newUrl)
}

/**
 * Check if URL already has concatenated values
 */
export const hasConcatValue = (
  pathname: string,
  originalSlug: string,
  basePath: string
): boolean => {
  const fullBasePath = `${basePath}/${originalSlug}`

  if (!pathname.startsWith(fullBasePath)) {
    return false
  }

  const remainingPath = pathname.slice(fullBasePath.length)
  if (!remainingPath || remainingPath === '/') {
    return false
  }

  // Check if there's anything after the original slug
  const concatPart = remainingPath.replace(/^\//, '')
  const originalParts = originalSlug.split('-')
  const pathParts = concatPart.split('-')

  return pathParts.length > originalParts.length
}

/**
 * Build collection URL with simple slug concatenation logic using concatValue
 */
export const buildCollectionUrl = (
  originalSlug: string,
  filters: FilterValues,
  basePath: string = '/collection',
  facets?: TFacet[],
  cardFacets?: TCardFacet[],
  slugAnalysis?: SlugAnalysis,
  currentPathname?: string,
  concatValue?: string | null,
  preserveParams?: string[]
): string => {
  let newSlug = originalSlug
  const params = new URLSearchParams()

  // Extract preserved params from current URL before processing filters
  const preservedParams: Record<string, string> = {}
  if (preserveParams && preserveParams.length > 0) {
    let currentUrl: URL | null = null
    if (typeof window !== 'undefined') {
      try {
        currentUrl = new URL(window.location.href)
      } catch {
        // Ignore URL parsing errors
      }
    } else if (currentPathname) {
      // For SSR, try to construct URL from currentPathname
      try {
        currentUrl = new URL(currentPathname, 'http://localhost')
      } catch {
        // Ignore URL parsing errors
      }
    }

    if (currentUrl) {
      preserveParams.forEach((param) => {
        const value = currentUrl!.searchParams.get(param)
        if (value) {
          preservedParams[param] = value
        }
      })
    }
  }

  // Sử dụng concatValue từ store - đơn giản và rõ ràng
  if (concatValue && concatValue !== null && concatValue !== '') {
    newSlug = `${originalSlug}-${concatValue}`
  }

  // Process all filters - đơn giản hóa logic
  Object.entries(filters).forEach(([key, value]) => {
    if (key === 'page' && Number(value) === 1) {
      return // Skip page=1
    }

    if (value === undefined || value === null || value === '') {
      return
    }

    // Always add page parameter if it exists and > 1
    if (key === 'page' && Number(value) > 1) {
      params.set(key, String(value))
      return
    }

    // Kiểm tra nếu đây là concat filter - chỉ skip khi có concatValue và đây là filter đã được xử lý
    // Chỉ skip khi key là 'concat' hoặc khi có concatValue và filter value chính xác match với concatValue
    if (concatValue && key === 'concat') {
      // Đây là concat filter - chỉ thêm vào query params nếu có multiple values
      if (Array.isArray(value) && value.length > 1) {
        params.set(key, value.join(','))
      }
      // Single value concat filters đã được xử lý ở trên (concatValue)
      return
    }

    // Kiểm tra nếu đây là concat filter khác - chỉ skip khi filter value chính xác match với concatValue
    if (concatValue && key !== 'page' && key !== 'sort' && key !== 'concat') {
      const filterValue = Array.isArray(value) ? value[0] : String(value)
      // Chỉ skip khi filter value chính xác match với concatValue (không phải contains)
      if (filterValue === concatValue) {
        // Đây là concat filter - chỉ thêm vào query params nếu có multiple values
        if (Array.isArray(value) && value.length > 1) {
          params.set(key, value.join(','))
        }
        // Single value concat filters đã được xử lý ở trên (concatValue)
        return
      }
    }

    // Add to query params cho các filter thông thường
    if (Array.isArray(value)) {
      if (value.length > 0) {
        params.set(key, value.join(','))
      }
    } else {
      params.set(key, String(value))
    }
  })

  // Restore preserved params after processing all filters
  // Skip preserved params that are already in filters (filters take precedence)
  Object.entries(preservedParams).forEach(([key, value]) => {
    // Don't overwrite if the param is already in filters (filters take precedence)
    if (
      !(key in filters) ||
      filters[key] === undefined ||
      filters[key] === null ||
      filters[key] === ''
    ) {
      params.set(key, value)
    }
  })

  const queryString = params.toString()
  const finalUrl = `${basePath}/${newSlug}${queryString ? `?${queryString}` : ''}`

  return finalUrl
}
